import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: any;
  loading: boolean;
  initializing: boolean;
  isAuthenticated: boolean;
  authReady: boolean;
  refreshAuth: () => Promise<void>;
  authInProgress: boolean;
  setAuthInProgress: (inProgress: boolean) => void;
  isBusinessUser: boolean; // Add business user flag
}

// Create a singleton auth state outside of React
let cachedUser: any = null;
let authInitialized = false;
let authListeners: Array<() => void> = [];
let globalAuthInProgress = false;
let isBusinessUser = false; // Add business user flag

// Function to notify all listeners of auth changes
const notifyAuthChange = () => {
  authListeners.forEach(listener => listener());
};

// Try to preload auth state from localStorage immediately
try {
  const storedSession = localStorage.getItem('supabase.auth.token');
  if (storedSession) {
    const parsedSession = JSON.parse(storedSession);
    if (parsedSession?.currentSession?.user) {
      cachedUser = parsedSession.currentSession.user;
      console.log('Preloaded user from localStorage:', parsedSession.currentSession.user.id);
    }
  }
} catch (e) {
  console.error('Error preloading auth state:', e);
}

const AuthContext = createContext<AuthContextType>({
  user: cachedUser,
  loading: !cachedUser,
  initializing: !authInitialized,
  isAuthenticated: !!cachedUser,
  authReady: !!cachedUser,
  refreshAuth: async () => {},
  authInProgress: false,
  setAuthInProgress: () => {},
  isBusinessUser: false, // Add business user flag
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);
  const [initializing, setInitializing] = useState(!authInitialized);
  const [isAuthenticated, setIsAuthenticated] = useState(!!cachedUser);
  const [authReady, setAuthReady] = useState(!!cachedUser);
  const [authInProgress, setAuthInProgress] = useState(globalAuthInProgress);
  const [businessUser, setBusinessUser] = useState(isBusinessUser);
  const authCheckComplete = useRef(false);
  const authStateChangeCount = useRef(0);
  const mountTime = useRef(Date.now());
  const sessionCheckTimeout = useRef<number | null>(null);

  // Update global auth in progress state
  const updateAuthInProgress = useCallback((inProgress: boolean) => {
    globalAuthInProgress = inProgress;
    setAuthInProgress(inProgress);
  }, []);

  // Function to check if user is a business user
  const checkBusinessRole = useCallback(async (userId: string) => {
    try {
      // Check if user has a business subscription or listing
      const { data: businessData, error: businessError } = await supabase
        .from('business_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (businessError) throw businessError;
      
      // If no business subscription, check if they have submitted a listing
      if (!businessData) {
        const { data: listingData, error: listingError } = await supabase
          .from('listings')
          .select('id')
          .eq('submitted_by', userId)
          .limit(1);
          
        if (listingError) throw listingError;
        
        const isBusiness = businessData || (listingData && listingData.length > 0);
        setBusinessUser(isBusiness);
        isBusinessUser = isBusiness;
        return isBusiness;
      }
      
      setBusinessUser(true);
      isBusinessUser = true;
      return true;
    } catch (err) {
      console.error('Error checking business role:', err);
      setBusinessUser(false);
      isBusinessUser = false;
      return false;
    }
  }, []);

  // Function to refresh auth state manually
  const refreshAuth = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Manually refreshing auth state...');
      
      const { data, error } = await supabase.auth.getSession();
      
      if (data.session?.user) {
        console.log('Auth refresh: User found', data.session.user.id);
        cachedUser = data.session.user;
        setUser(data.session.user);
        setIsAuthenticated(true);
        
        // Check if user is a business user
        await checkBusinessRole(data.session.user.id);
      } else {
        console.log('Auth refresh: No user found', error?.message || 'No session');
        cachedUser = null;
        setUser(null);
        setIsAuthenticated(false);
        setBusinessUser(false);
        isBusinessUser = false;
      }
    } catch (err) {
      console.error('Auth refresh error:', err);
      cachedUser = null;
      setUser(null);
      setIsAuthenticated(false);
      setBusinessUser(false);
      isBusinessUser = false;
    } finally {
      setLoading(false);
    }
  }, [checkBusinessRole]);

  // Function to check session with timeout
  const checkSessionWithTimeout = useCallback(async () => {
    // Set a timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      sessionCheckTimeout.current = window.setTimeout(() => {
        reject(new Error('Session check timed out'));
      }, 5000); // 5 second timeout
    });

    try {
      // Race between the actual session check and the timeout
      const result = await Promise.race([
        supabase.auth.getSession(),
        timeoutPromise
      ]);
      
      // Clear timeout if session check completed
      if (sessionCheckTimeout.current) {
        clearTimeout(sessionCheckTimeout.current);
        sessionCheckTimeout.current = null;
      }
      
      return result;
    } catch (error) {
      console.error('Session check timed out or failed:', error);
      // Clear timeout if it exists
      if (sessionCheckTimeout.current) {
        clearTimeout(sessionCheckTimeout.current);
        sessionCheckTimeout.current = null;
      }
      
      // Return a default response
      return { data: { session: null }, error };
    }
  }, []);

  useEffect(() => {
    console.log(`AuthProvider mounted at ${new Date(mountTime.current).toISOString()}, initializing auth state`);
    
    // Get initial session
    const getSession = async () => {
      try {
        console.log('Starting auth initialization...');
        
        // Check localStorage first for faster initial render
        try {
          const storedSession = localStorage.getItem('supabase.auth.token');
          if (storedSession) {
            const parsedSession = JSON.parse(storedSession);
            if (parsedSession?.currentSession?.user) {
              cachedUser = parsedSession.currentSession.user;
              setUser(parsedSession.currentSession.user);
              setIsAuthenticated(true);
              console.log('Using cached session from localStorage:', parsedSession.currentSession.user.id);
              
              // Check if user is a business user
              await checkBusinessRole(parsedSession.currentSession.user.id);
            }
          }
        } catch (e) {
          console.error('Error parsing localStorage session:', e);
        }

        // Then verify with actual API call
        const { data: { session }, error } = await checkSessionWithTimeout();
        
        if (session?.user) {
          cachedUser = session.user;
          setUser(session.user);
          setIsAuthenticated(true);
          console.log('Verified session with API:', session.user.id);
          
          // Check if user is a business user
          await checkBusinessRole(session.user.id);
          
          // Ensure localStorage is updated with the latest session
          try {
            const storedData = localStorage.getItem('supabase.auth.token');
            if (storedData) {
              const parsedData = JSON.parse(storedData);
              parsedData.currentSession = session;
              localStorage.setItem('supabase.auth.token', JSON.stringify(parsedData));
            }
          } catch (e) {
            console.error('Error updating localStorage:', e);
          }
        } else {
          if (error) {
            console.error('Session error:', error);
          }
          cachedUser = null;
          setUser(null);
          setIsAuthenticated(false);
          setBusinessUser(false);
          isBusinessUser = false;
          console.log('No valid session found');
        }
      } catch (err) {
        console.error('Session check error:', err);
        cachedUser = null;
        setUser(null);
        setIsAuthenticated(false);
        setBusinessUser(false);
        isBusinessUser = false;
      } finally {
        setLoading(false);
        setInitializing(false);
        authInitialized = true;
        authCheckComplete.current = true;
        setAuthReady(true);
        console.log('Auth initialization complete');
      }
    };

    if (!authCheckComplete.current) {
      getSession();
    }

    // Register this component as a listener
    const notifyChange = () => {
      setUser(cachedUser);
      setIsAuthenticated(!!cachedUser);
      setBusinessUser(isBusinessUser);
    };
    
    authListeners.push(notifyChange);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      authStateChangeCount.current += 1;
      console.log(`Auth state change (${authStateChangeCount.current}): ${event}`, session?.user?.id || 'No session');
      
      if (session?.user) {
        cachedUser = session.user;
        setUser(session.user);
        setIsAuthenticated(true);
        
        // Check if user is a business user
        await checkBusinessRole(session.user.id);
        
        // Ensure localStorage is updated
        try {
          const storedData = localStorage.getItem('supabase.auth.token');
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            parsedData.currentSession = session;
            localStorage.setItem('supabase.auth.token', JSON.stringify(parsedData));
          }
        } catch (e) {
          console.error('Error updating localStorage:', e);
        }
      } else {
        cachedUser = null;
        setUser(null);
        setIsAuthenticated(false);
        setBusinessUser(false);
        isBusinessUser = false;
      }
      
      setLoading(false);
      authInitialized = true;
      authCheckComplete.current = true;
      setAuthReady(true);
      
      // Notify all listeners
      notifyAuthChange();
    });

    return () => {
      console.log('Unsubscribing from auth state changes');
      subscription.unsubscribe();
      
      // Remove this component from listeners
      authListeners = authListeners.filter(listener => listener !== notifyChange);
      
      // Clear any pending timeouts
      if (sessionCheckTimeout.current) {
        clearTimeout(sessionCheckTimeout.current);
      }
    };
  }, [checkSessionWithTimeout, checkBusinessRole]);

  // Debug logging
  useEffect(() => {
    console.log('Auth state updated:', { 
      isAuthenticated, 
      loading, 
      initializing,
      authReady,
      authInProgress,
      userId: user?.id,
      isBusinessUser: businessUser,
      timestamp: new Date().toISOString()
    });
  }, [isAuthenticated, loading, initializing, authReady, authInProgress, user, businessUser]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      initializing, 
      isAuthenticated, 
      authReady,
      refreshAuth,
      authInProgress,
      setAuthInProgress: updateAuthInProgress,
      isBusinessUser: businessUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};